import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Button, Grid2 as Grid, styled } from '@mui/material';

import { Language } from '@mewa/types';
import { ResponsiveGrid, SectionDivider } from '@mewa/ui';

import t from '../../../libs/translate';
import { SearchPageTranslations } from '../../components/header/types';
import {
  PageFindResultData,
  searchResultsAreBlogArticles,
  searchResultsArePressReleases,
  searchResultsAreProducts,
} from '../../components/search/types';
import { SearchResultBlogArticles } from './search-result-blog-articles';
import { SearchResultBrandTopics } from './search-result-brand-topics';
import {
  SearchResultCategories,
  ServiceSegmentImages,
} from './search-result-categories';
import { SearchResultPressReleases } from './search-result-press-releases';
import { SearchResultProducts } from './search-result-products';

export enum SectionType {
  products = 'products',
  categories = 'categories',
  brandTopics = 'brandTopics',
  pressReleases = 'pressReleases',
  blogArticles = 'blogArticles',
}

type Props = {
  title: string;
  searchResults: PageFindResultData[];
  sectionType: SectionType;
  langPrefix: string;
  serviceSegmentImages?: ServiceSegmentImages;
  translations: SearchPageTranslations;
  detailsPageLabel: string;
  blogSlug: string;
  locale: string;
  lang: Language;
};

const SearchSection = ({
  title,
  searchResults,
  sectionType,
  langPrefix,
  serviceSegmentImages,
  translations,
  detailsPageLabel,
  blogSlug,
  lang,
  locale,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const renderedSections = useMemo(() => {
    switch (sectionType) {
      case SectionType.products:
        if (searchResultsAreProducts(searchResults)) {
          return (
            <SearchResultProducts
              results={searchResults}
              langPrefix={langPrefix}
              expanded={isExpanded}
              detailsPageLabel={detailsPageLabel}
            />
          );
        }
        break;
      case SectionType.categories:
        return (
          <SearchResultCategories
            results={searchResults}
            expanded={isExpanded}
            serviceSegmentImages={serviceSegmentImages}
            translations={translations}
          />
        );
      case SectionType.pressReleases:
        if (searchResultsArePressReleases(searchResults)) {
          return (
            <SearchResultPressReleases
              results={searchResults}
              expanded={isExpanded}
              locale={locale}
            />
          );
        }
        break;
      case SectionType.blogArticles:
        if (searchResultsAreBlogArticles(searchResults)) {
          return (
            <SearchResultBlogArticles
              results={searchResults}
              expanded={isExpanded}
              lang={lang}
              langPrefix={langPrefix}
              locale={locale}
              blogSlug={blogSlug}
              translations={translations}
            />
          );
        }
        break;
      case SectionType.brandTopics:
        return <SearchResultBrandTopics results={searchResults} />;
    }
    return null;
  }, [
    isExpanded,
    langPrefix,
    searchResults,
    sectionType,
    serviceSegmentImages,
    detailsPageLabel,
    translations,
    blogSlug,
    lang,
    locale,
  ]);

  const scrollingIsEnabled = useRef(false);

  useEffect(() => {
    if (scrollingIsEnabled.current && !isExpanded) {
      document
        .getElementById(sectionType)
        ?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isExpanded, sectionType, scrollingIsEnabled]);

  return (
    <Container id={sectionType}>
      <ResponsiveGrid>
        <Grid size={12}>
          <SectionDivider title={title} />
        </Grid>
      </ResponsiveGrid>

      {renderedSections}

      <ResponsiveGrid>
        <Grid size={12}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: '50px',
            }}
          >
            {searchResults.length > 4 && (
              <Button
                onClick={() => {
                  scrollingIsEnabled.current = true;
                  setIsExpanded(!isExpanded);
                }}
              >
                {isExpanded
                  ? t(translations, 'search_page_result_show_less')
                  : t(translations, 'search_page_result_show_more')}
              </Button>
            )}
          </div>
        </Grid>
      </ResponsiveGrid>
    </Container>
  );
};

export default SearchSection;

const Container = styled('div')(({ theme }) => ({
  marginTop: '36px',

  [theme.breakpoints.up('sm')]: {
    marginTop: '50px',
  },
}));
